import { useMemo, useState } from "react";
import { ArrowDownAZ, ArrowUpAZ, Calendar1, CalendarSync } from "lucide-react";
import Container from "../Container.tsx";
import {
  BookingResponseDto,
  useUserBookingsQuery,
} from "../../api/bookingApi.ts";
import {
  Subscription,
  useUserSubscriptionsQuery,
} from "../../api/subscriptionApi.ts";
import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import { LoadingSpinner } from "../util/LoadingSpinner.tsx";
import UserBookingCard from "./UserBookingCard.tsx";
import UserSubscriptionCard from "../subscription/UserSubscriptionCard.tsx";
import DashboardEmptyState from "../dashboard/DashboardEmptyState.tsx";
import { Tabs } from "../Tabs.tsx";

type BookingSortBy = "createdAt" | "timeslot";
type SortDir = "asc" | "desc";

interface SortOption<T extends string> {
  value: `${T}-${SortDir}`;
  label: string;
}

const bookingSortOptions: SortOption<BookingSortBy>[] = [
  { value: "createdAt-desc", label: "Created: Newest first" },
  { value: "createdAt-asc", label: "Created: Oldest first" },
  { value: "timeslot-asc", label: "Lesson: Soonest first" },
  { value: "timeslot-desc", label: "Lesson: Latest first" },
];

const subscriptionSortOptions: SortOption<"createdAt">[] = [
  { value: "createdAt-desc", label: "Created: Newest first" },
  { value: "createdAt-asc", label: "Created: Oldest first" },
];

function compareInstants(
  a?: string,
  b?: string,
  dir: SortDir = "desc",
): number {
  const aTime = a ? new Date(a).getTime() : 0;
  const bTime = b ? new Date(b).getTime() : 0;
  return dir === "asc" ? aTime - bTime : bTime - aTime;
}

function earliestSlot(booking: BookingResponseDto): number {
  const times = booking.timeSlots
    .filter((s) => s.status !== "CANCELLED")
    .map((s) => new Date(s.from).getTime());
  return times.length > 0 ? Math.min(...times) : Number.MAX_SAFE_INTEGER;
}

function sortBookings(
  bookings: BookingResponseDto[],
  sortBy: BookingSortBy,
  sortDir: SortDir,
): BookingResponseDto[] {
  const list = [...bookings];
  if (sortBy === "timeslot") {
    list.sort((a, b) => {
      const diff = earliestSlot(a) - earliestSlot(b);
      return sortDir === "asc" ? diff : -diff;
    });
  } else {
    list.sort((a, b) => compareInstants(a.createdAt, b.createdAt, sortDir));
  }
  return list;
}

function sortSubscriptions(
  subscriptions: Subscription[],
  sortDir: SortDir,
): Subscription[] {
  return [...subscriptions].sort((a, b) =>
    compareInstants(a.createdAt, b.createdAt, sortDir),
  );
}

function SortSelect<T extends string>({
  value,
  onChange,
  options,
}: {
  value: string;
  onChange: (val: string) => void;
  options: SortOption<T>[];
}) {
  const isAsc = value.endsWith("-asc");
  return (
    <div className="flex items-center gap-1.5">
      {isAsc ? (
        <ArrowUpAZ className="text-soluud-text-tertiary h-3.5 w-3.5 shrink-0" />
      ) : (
        <ArrowDownAZ className="text-soluud-text-tertiary h-3.5 w-3.5 shrink-0" />
      )}
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="border-soluud-border bg-soluud-background-card text-soluud-text-secondary focus:ring-soluud-primary cursor-pointer rounded-lg border px-2 py-1 text-xs focus:ring-1 focus:outline-none"
      >
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
    </div>
  );
}

function UserBookings() {
  const [bookingSort, setBookingSort] = useState("createdAt-desc");
  const [subscriptionSort, setSubscriptionSort] = useState("createdAt-desc");

  const [bookingSortBy, bookingSortDir] = bookingSort.split("-") as [
    BookingSortBy,
    SortDir,
  ];
  const [, subscriptionSortDir] = subscriptionSort.split("-") as [
    "createdAt",
    SortDir,
  ];

  const {
    data: bookings,
    isLoading: isLoadingBookings,
    error: bookingsError,
  } = useUserBookingsQuery();
  const {
    data: subscriptions,
    isLoading: isLoadingSubscriptions,
    error: subscriptionsError,
  } = useUserSubscriptionsQuery();

  const sortedBookings = useMemo(
    () => sortBookings(bookings ?? [], bookingSortBy, bookingSortDir),
    [bookings, bookingSortBy, bookingSortDir],
  );

  const sortedSubscriptions = useMemo(
    () => sortSubscriptions(subscriptions ?? [], subscriptionSortDir),
    [subscriptions, subscriptionSortDir],
  );

  if (isLoadingBookings || isLoadingSubscriptions) {
    return <LoadingSpinner loadingText={t`Loading...`} />;
  }

  if (bookingsError || subscriptionsError) {
    return (
      <Container>
        <div className="rounded-lg border border-red-200 bg-red-50 p-4">
          <p className="text-red-800">
            <Trans>Error loading your bookings. Please try again.</Trans>
          </p>
        </div>
      </Container>
    );
  }

  const totalBookings = sortedBookings.length;
  const totalSubscriptions = sortedSubscriptions.length;

  return (
    <Container>
      <Tabs
        paramKey="tab"
        defaultValue="bookings"
        rightSlot={(activeTab) =>
          activeTab === "bookings" ? (
            <SortSelect
              value={bookingSort}
              onChange={setBookingSort}
              options={bookingSortOptions}
            />
          ) : (
            <SortSelect
              value={subscriptionSort}
              onChange={setSubscriptionSort}
              options={subscriptionSortOptions}
            />
          )
        }
      >
        <Tabs.Tab
          value="bookings"
          label={
            <span className="flex items-center gap-1.5">
              <Calendar1 className="h-4 w-4" />
              <Trans>Bookings</Trans>
              <span className="text-soluud-text-secondary text-xs font-normal">
                ({totalBookings})
              </span>
            </span>
          }
        >
          {sortedBookings.length > 0 ? (
            <div className="flex flex-col gap-2">
              {sortedBookings.map((booking) => (
                <UserBookingCard key={booking.id} booking={booking} />
              ))}
            </div>
          ) : (
            <DashboardEmptyState
              icon={Calendar1}
              message={<Trans>No bookings yet.</Trans>}
              linkTo="/search"
              linkLabel={<Trans>Find a Teacher</Trans>}
            />
          )}
        </Tabs.Tab>

        <Tabs.Tab
          value="subscriptions"
          label={
            <span className="flex items-center gap-1.5">
              <CalendarSync className="h-4 w-4" />
              <Trans>Subscriptions</Trans>
              <span className="text-soluud-text-secondary text-xs font-normal">
                ({totalSubscriptions})
              </span>
            </span>
          }
        >
          {sortedSubscriptions.length > 0 ? (
            <div className="flex flex-col gap-2">
              {sortedSubscriptions.map((subscription) => (
                <UserSubscriptionCard
                  key={subscription.id}
                  subscription={subscription}
                />
              ))}
            </div>
          ) : (
            <DashboardEmptyState
              icon={CalendarSync}
              message={<Trans>No subscriptions yet.</Trans>}
              linkTo="/search"
              linkLabel={<Trans>Find a Teacher</Trans>}
            />
          )}
        </Tabs.Tab>
      </Tabs>
    </Container>
  );
}

export default UserBookings;
